import React from "react";
import { FormControl, Select, MenuItem, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InputLabel from "@mui/material/InputLabel";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";

interface StyledExpandMoreIconProps {
  disabled?: boolean;
}

const DISABLED_COLOR = "#bdbdbd"; // Consistent gray color for disabled state

const SelectStyled = styled(Select)(({ theme, value, disabled }) => ({
  backgroundColor: theme.palette.background.default,
  "& .MuiSelect-select:focus": {
    width: "100%",
    backgroundColor: theme.palette.background.default,
  },
  "& .MuiSelect-select:not(:focus)": {
    backgroundColor: theme.palette.background.default,
    width: "100%",
  },
  height: "62px",
  "& .MuiInputBase-input": {
    color: disabled ? DISABLED_COLOR : theme.palette.primary.main,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: disabled
      ? DISABLED_COLOR
      : value !== "0" && value !== "" && value !== null
      ? theme.palette.primary.light
      : theme.palette.primary.contrastText,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: disabled ? DISABLED_COLOR : theme.palette.primary.light,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: disabled ? DISABLED_COLOR : theme.palette.primary.light,
  },
  "&.Mui-disabled": {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: DISABLED_COLOR,
    },
    "& .MuiSelect-icon": {
      color: DISABLED_COLOR,
    },
  },
}));

const Icon = styled(ExpandMoreIcon, {
  shouldForwardProp: (prop) => prop !== "disabled",
})<StyledExpandMoreIconProps>(({ disabled }) => ({
  color: disabled ? DISABLED_COLOR : undefined,
}));

const StyledInputLabel = styled(InputLabel)(({ disabled }) => ({
  color: disabled ? DISABLED_COLOR : "#333333",
}));

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  color: theme.palette.primary.contrastText,
}));

interface BookingDurationSelectProps {
  value: string | null;
  onChange: (value: string) => void;
  disabled: boolean;
  label: string;
  placeholder?: string;
  limit?: number | undefined;
  data: Array<string>;
}

const VoucherAmountSelect: React.FC<BookingDurationSelectProps> = (props) => {
  const { t } = useTranslation();
  const tenantConfig = useSelector(
    (state: RootState) => state.tenantConfig.config
  );

  const shouldShrinkLabel = props.value !== "0" && props.value !== "" && props.value !== null;
  const showPlaceholder = props.value === "" || props.value === null;

  const renderSelectedValue = (selectedValue: unknown) => {
    if (showPlaceholder) {
      return <em style={{ color: "#bdbdbd" }}>{props.placeholder}</em>;
    }
    return <em>{selectedValue as number}</em>;
  };

  const data = props.data;
  return (
    <FormControl>
      <StyledInputLabel
        id="demo-select-large-label"
        disabled={props.disabled}
        shrink={shouldShrinkLabel}
      >
        {props.label}
      </StyledInputLabel>
      <SelectStyled
        labelId="demo-select-large-label"
        id="demo-select-small"
        value={props.value ?? ""} // Convert null to empty string for MUI Select
        disabled={props.disabled}
        displayEmpty
        label={shouldShrinkLabel ? props.label : null}
        onChange={(event) => props.onChange(event.target.value as string)}
        IconComponent={(iconProps) => (
          <Icon {...iconProps} disabled={props.disabled} />
        )}
        renderValue={renderSelectedValue}
      >
        {/* <MenuItemStyled value="" disabled>
          <Typography variant="body2" color={DISABLED_COLOR}>
            {props.placeholder}
          </Typography>
        </MenuItemStyled> */}
        {data.map((quantity) => (
          <MenuItemStyled key={quantity} value={quantity}>
            <Typography variant="body2">
              {tenantConfig.currencySymbol}
              {quantity}
            </Typography>
          </MenuItemStyled>
        ))}
        <MenuItemStyled value={t("general.voucher_purchase.custom_amount")}>
          <Typography variant="body2">{t("general.voucher_purchase.custom_amount")}</Typography>
        </MenuItemStyled>
      </SelectStyled>
    </FormControl>
  );
};

export default VoucherAmountSelect;